// PidTableShell.jsx — shared container-table shell for the five Package Index admin pages.
// Header block (title + entry count, Add Entry, single global search), sortable columns,
// whole-row edit trigger (interactive cells excepted), responsive down to a stacked-card
// mode at <=640px via CSS-only restyling of the same DOM.
// Plan: Cerebral wiki/webapp/the-vault/plans/the-vault-pid-tables-ux-2026-07.md
//
// Column config: { key, label, render?, sortValue?, interactive?, emphasis?, cardRole? }
//   interactive — cell clicks do not trigger the row (e.g. the StatusPicker cell)
//   emphasis   — semibold cell text (entity-name columns)
//   cardRole   — 'title' | 'status' placement in the <=640px card layout; others render
//                as label/value lines using the column label
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';

const DEBOUNCE_MS = 300;

const ShellRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media (min-width: 1800px) {
    font-size: 16px;
  }
`;

const HeadBlock = styled.div`
  margin: 2px 4px 14px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
`;

const ShellTitle = styled.h2`
  margin: 0;
  font-family: var(--font-heading);
  font-size: 1.5em;
  font-weight: 700;
  color: var(--green-800);
  -webkit-font-smoothing: antialiased;
`;

const RowCount = styled.span`
  font-size: 0.8em;
  font-weight: 600;
  color: var(--ink-a45);
  font-variant-numeric: tabular-nums;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AddButton = styled.button`
  font: inherit;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 16px;
  background: var(--green-700);
  color: var(--white);
  transition: background 0.2s ease;

  &:hover { background: var(--green-800); }
  &:focus-visible { outline: 2px solid var(--green-a45); outline-offset: 2px; }
`;

/* Idle: quiet border, grey magnifier. Focused: green border + 1px ring (app-wide green
   focus). Query present but unfocused: green border, no ring — "this list is filtered"
   stays visible after focus leaves. */
const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 280px;
  max-width: 100%;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  border: 1px solid ${({ $active }) => ($active ? 'var(--green-800)' : 'var(--white-a66)')};
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: var(--green-800);
    box-shadow: 0 0 0 1px var(--green-800);
  }

  @media (max-width: 720px) { width: 100%; }
  @media (min-width: 1800px) { width: 330px; }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font: inherit;
  color: var(--color-text);
  padding: 8px 4px 8px 12px;
  outline: none;

  &::placeholder { color: var(--ink-a45); }
`;

const ClearButton = styled.button`
  display: inline-flex;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--ink-a45);
  padding: 4px;
  border-radius: 6px;
  line-height: 0;

  &:hover { color: var(--color-text); background: var(--shadow-a05); }
  &:focus-visible { outline: 2px solid var(--green-a45); }
`;

const SearchIcon = styled.span`
  display: inline-flex;
  padding: 0 10px 0 2px;
  line-height: 0;
  transition: color 0.2s ease;
  color: ${({ $active }) => ($active ? 'var(--green-800)' : 'var(--grey-500)')};

  ${SearchBox}:focus-within & { color: var(--green-800); }
`;

const TableCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--grey-200);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-a08);
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TableScroll = styled.div`
  flex: 1;
  overflow: auto;
  overscroll-behavior: contain;
`;

const ShellTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const THead = styled.thead`
  @media (max-width: 640px) { display: none; }
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--green-800);
  color: var(--white);
  font-family: var(--font-heading);
  font-size: 0.85em;
  font-weight: 600;
  text-align: left;
  padding: 12px 16px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  &:hover { background: var(--green-900); }
  &:focus-visible { outline: 2px solid var(--white-a66); outline-offset: -3px; }

  @media (max-width: 900px) { padding: 10px 12px; }
`;

const GlyphWrap = styled.span`
  display: inline-block;
  margin-left: 6px;
  vertical-align: -2px;
  opacity: ${({ $active }) => ($active ? 1 : 0.35)};

  ${Th}:hover & { opacity: ${({ $active }) => ($active ? 1 : 0.7)}; }
`;

const Tr = styled.tr`
  cursor: pointer;

  &:nth-child(even) { background: var(--cream-50); }
  &:hover { background: var(--green-a08); }
  &:focus-visible { outline: 2px solid var(--green-a45); outline-offset: -2px; }
  &:last-child td { border-bottom: none; }

  @media (max-width: 640px) {
    display: block;
    position: relative;
    padding: 12px 16px;
    border-bottom: 1px solid var(--grey-100);
  }
`;

const Td = styled.td`
  padding: 13px 16px;
  border-bottom: 1px solid var(--grey-100);
  vertical-align: middle;

  @media (max-width: 900px) { padding: 10px 12px; }

  @media (max-width: 640px) {
    display: flex;
    gap: 10px;
    padding: 2px 0;
    border-bottom: none;

    &::before {
      content: attr(data-label);
      flex: 0 0 96px;
      font-size: 0.66em;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--ink-a45);
      padding-top: 3px;
    }

    &[data-cardrole='title'] {
      display: block;
      font-size: 1.05em;
      font-weight: 600;
      margin: 0 96px 2px 0;
    }
    &[data-cardrole='title']::before { display: none; }

    &[data-cardrole='status'] {
      position: absolute;
      top: 12px;
      right: 14px;
      display: block;
      padding: 0;
    }
    &[data-cardrole='status']::before { display: none; }
  }
`;

const MessageCell = styled.td`
  text-align: center;
  padding: 38px 16px;
  color: var(--ink-a60);

  button {
    border: none;
    background: none;
    font: inherit;
    font-weight: 600;
    color: var(--green-700);
    cursor: pointer;
    text-decoration: underline;
    padding: 0 2px;
  }
  button:focus-visible { outline: 2px solid var(--green-a45); }
`;

const PadMessage = styled.div`
  padding: 2rem;
  color: var(--ink-a60);
`;

/* Commodity chips for the flattened Methods/Labels rows — the Size Options chip
   recipe. Non-interactive by design (no fake affordance on read-only aggregates). */
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ChipTag = styled.span`
  font-size: 0.82em;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 7px;
  white-space: nowrap;
  border: 1px solid var(--green-a60);
  background: var(--green-a15);
  color: var(--green-800);
`;

export const CommodityChips = ({ names }) => (
  <ChipRow>
    {(names || []).map((name, i) => <ChipTag key={`${name}-${i}`}>{name}</ChipTag>)}
  </ChipRow>
);

const PidTableShell = ({
  title,
  columns,
  rows,
  rowKey,
  searchText,
  defaultSort = null,
  onAdd,
  addLabel = 'Add Entry',
  onRowClick,
  rowAriaLabel,
  loading = false,
  loadingText = 'Loading...',
  emptyText = 'No records found.',
}) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(defaultSort);
  const debounceRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleInput = (value) => {
    setInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(value.trim().toLowerCase()), DEBOUNCE_MS);
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setInput('');
    setQuery('');
  };

  /* Case-insensitive contains-sequence over the row's visible text only. */
  const filtered = useMemo(() => {
    if (!query) return rows;
    return rows.filter((r) => searchText(r).toLowerCase().includes(query));
  }, [rows, query, searchText]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const value = col.sortValue || ((r) => r[col.key]);
    return [...filtered].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const cmp = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va ?? '').localeCompare(String(vb ?? ''), undefined, { sensitivity: 'base', numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  const toggleSort = (key) => {
    setSort((prev) => (prev && prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));
  };

  const hasQuery = input.trim() !== '';

  return (
    <ShellRoot>
      <HeadBlock>
        <TitleRow>
          <ShellTitle>{title}</ShellTitle>
          <RowCount aria-live="polite">
            {loading ? '' : (query ? `${filtered.length} of ${rows.length}` : `${rows.length} entries`)}
          </RowCount>
        </TitleRow>
        <ActionsRow>
          <AddButton type="button" onClick={onAdd}>＋ {addLabel}</AddButton>
          <SearchBox $active={hasQuery}>
            <SearchInput
              type="text"
              value={input}
              placeholder="Search..."
              aria-label={`Search ${title}`}
              autoComplete="off"
              onChange={(e) => handleInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && input !== '') {
                  e.stopPropagation();
                  clearSearch();
                }
              }}
            />
            {hasQuery && (
              <ClearButton type="button" aria-label="Clear search" onClick={clearSearch}>
                <X size={14} />
              </ClearButton>
            )}
            <SearchIcon $active={hasQuery} aria-hidden="true"><Search size={16} /></SearchIcon>
          </SearchBox>
        </ActionsRow>
      </HeadBlock>

      <TableCard>
        {loading ? (
          <PadMessage>{loadingText}</PadMessage>
        ) : rows.length === 0 ? (
          <PadMessage>{emptyText}</PadMessage>
        ) : (
          <TableScroll>
            <ShellTable>
              <THead>
                <tr>
                  {columns.map((col) => {
                    const active = sort && sort.key === col.key;
                    return (
                      <Th
                        key={col.key}
                        tabIndex={0}
                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                        aria-label={`Sort by ${col.label}`}
                        onClick={() => toggleSort(col.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSort(col.key);
                          }
                        }}
                      >
                        {col.label}
                        <GlyphWrap $active={!!active}>
                          {active && sort.dir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                        </GlyphWrap>
                      </Th>
                    );
                  })}
                </tr>
              </THead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <MessageCell colSpan={columns.length}>
                      No matches for “{input.trim()}” —{' '}
                      <button type="button" onClick={clearSearch}>Clear search</button>
                    </MessageCell>
                  </tr>
                ) : sorted.map((row) => (
                  <Tr
                    key={rowKey(row)}
                    tabIndex={0}
                    aria-label={rowAriaLabel ? rowAriaLabel(row) : undefined}
                    onClick={() => onRowClick(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }}
                  >
                    {columns.map((col) => (
                      <Td
                        key={col.key}
                        data-label={col.label}
                        data-cardrole={col.cardRole || undefined}
                        style={col.emphasis ? { fontWeight: 600 } : undefined}
                        onClick={col.interactive ? (e) => e.stopPropagation() : undefined}
                        onKeyDown={col.interactive ? (e) => e.stopPropagation() : undefined}
                      >
                        {col.render ? col.render(row) : (row[col.key] ?? '')}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
            </ShellTable>
          </TableScroll>
        )}
      </TableCard>
    </ShellRoot>
  );
};

export default PidTableShell;
